/**
 * The WorkOS access token sealed inside a session lives about five minutes,
 * while the session itself lasts much longer. Without knowing when the token
 * lapses, the client only learns about it from a denied API request, then
 * refreshes and retries. The server reports the remaining lifetime on every
 * session verify and refresh; the client turns it into a local deadline and
 * refreshes just before it, so routine requests never carry an expired token.
 *
 * The deadline is a non-secret timestamp. It lives in localStorage so every
 * tab and window sharing the session cookie sees one refresh, with an
 * in-memory fallback where storage is unavailable.
 */

/** Response field carrying milliseconds until the current access token expires. */
export const ACCESS_TOKEN_LIFETIME_FIELD = "accessTokenExpiresInMs";

/** Refresh this long before the deadline, covering request latency. */
export const ACCESS_TOKEN_REFRESH_LEAD_MS = 30_000;

const STORAGE_KEY = "smartmanager_access_token_expires_at";
let memoryExpiresAt: number | null = null;

function storage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> | null {
  try {
    return typeof globalThis.localStorage === "undefined" ? null : globalThis.localStorage;
  } catch {
    return null;
  }
}

/** Reads the reported lifetime from a verify or refresh body. */
export function readAccessTokenLifetime(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const value = (body as Record<string, unknown>)[ACCESS_TOKEN_LIFETIME_FIELD];
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Records the deadline from a successful verify or refresh body. A body
 * without a lifetime clears the deadline: an unknown expiry falls back to the
 * 401-then-refresh path rather than trusting a deadline for an older token.
 */
export function recordAccessTokenExpiry(body: unknown, now = Date.now()): void {
  const lifetime = readAccessTokenLifetime(body);
  if (lifetime === null) {
    clearAccessTokenExpiry();
    return;
  }
  const expiresAt = now + lifetime;
  memoryExpiresAt = expiresAt;
  try {
    storage()?.setItem(STORAGE_KEY, String(expiresAt));
  } catch {
    // Storage denied: the in-memory deadline still covers this window.
  }
}

export function clearAccessTokenExpiry(): void {
  memoryExpiresAt = null;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Storage denied: nothing persisted to clear.
  }
}

export function accessTokenExpiresAt(): number | null {
  const store = storage();
  if (store) {
    // Readable storage is the shared source of truth across windows.
    try {
      const stored = store.getItem(STORAGE_KEY);
      if (stored === null) return null;
      const parsed = Number(stored);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      // Fall through to the in-memory deadline.
    }
  }
  return memoryExpiresAt;
}

/** True once the known deadline is within the refresh lead. Unknown is never due. */
export function accessTokenRefreshDue(now = Date.now()): boolean {
  const expiresAt = accessTokenExpiresAt();
  return expiresAt !== null && now >= expiresAt - ACCESS_TOKEN_REFRESH_LEAD_MS;
}
